import { useId } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import type { Control, FieldErrors, FieldPath, FieldValues } from 'react-hook-form';
import { useFormContext, useFormState } from 'react-hook-form';
import * as z from 'zod';

import { FormControl, FormField, FormItem } from '@/components/core/form';
import { Input } from '@/components/core/input';
import { cn } from '@/shared/helpers';

export const addressFormFieldSchema = z.object({
  address1: z.string().trim().min(1, 'Enter an address'),
  address2: z.string().trim(),
  state: z.string().trim(),
  city: z.string().trim(),
  country: z.string().trim().min(1, 'Enter a country'),
  postcode: z.string().trim(),
});

export const addressFormFieldDefaultValues = {
  address1: '',
  address2: '',
  state: '',
  city: '',
  country: '',
  postcode: '',
};

export const contactAddressFormFieldSchema = addressFormFieldSchema.extend({
  companyName: z.string().trim(),
  contactName: z.string().trim(),
  contactEmail: z.string().trim(),
  contactNumber: z.string().trim(),
});

export const contactAddressFormFieldDefaultValues = {
  companyName: '',
  contactName: '',
  contactEmail: '',
  contactNumber: '',
  ...addressFormFieldDefaultValues,
};

type ContactAddressFieldName = keyof typeof contactAddressFormFieldDefaultValues;

type AddressFormFieldProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  ComponentProps<'fieldset'>,
  'children' | 'disabled' | 'name'
> & {
  control?: Control<TFieldValues>;
  name?: FieldPath<TFieldValues>;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  hideError?: boolean;
};

type AddressInputFieldProps<TFieldValues extends FieldValues> = {
  control?: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  placeholder: string;
  autoComplete: string;
  disabled?: boolean;
  className?: string;
};

const addressFields = ['address1', 'address2', 'state', 'city', 'country', 'postcode'] as const;
const contactAddressFields = [
  'companyName',
  'contactName',
  'contactNumber',
  'contactEmail',
  ...addressFields,
] as const;

function getFieldName<TFieldValues extends FieldValues>(
  name: FieldPath<TFieldValues> | undefined,
  fieldName: ContactAddressFieldName,
) {
  return (name ? `${name}.${fieldName}` : fieldName) as FieldPath<TFieldValues>;
}

function getErrorMessage(errors: FieldErrors, name: string) {
  const error = name.split('.').reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object') {
      return undefined;
    }

    return (current as Record<string, unknown>)[key];
  }, errors);

  if (!error || typeof error !== 'object' || !('message' in error)) {
    return undefined;
  }

  const { message } = error as { message?: unknown };
  return typeof message === 'string' ? message : undefined;
}

function AddressInputField<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  autoComplete,
  disabled,
  className,
}: AddressInputFieldProps<TFieldValues>) {
  return (
    <FormField
      control={control}
      name={name}
      disabled={disabled}
      render={({ field }) => (
        <FormItem className={cn('gap-0', className)}>
          <FormControl>
            <Input
              {...field}
              value={field.value ?? ''}
              disabled={field.disabled}
              aria-label={label}
              placeholder={placeholder}
              autoComplete={autoComplete}
              className="rounded-none border-0 bg-transparent focus-visible:ring-0 aria-invalid:text-destructive aria-invalid:ring-0 aria-invalid:placeholder:text-destructive"
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

function AddressFormFieldFrame<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  label = 'Address',
  description,
  disabled,
  hideError,
  className,
  errorFields,
  children,
  ...props
}: AddressFormFieldProps<TFieldValues> & {
  errorFields: readonly ContactAddressFieldName[];
  children: (control: Control<TFieldValues>) => ReactNode;
}) {
  const descriptionId = useId();
  const errorId = useId();
  const form = useFormContext<TFieldValues>();
  const formControl = control ?? form.control;
  const fieldNames = errorFields.map((fieldName) => getFieldName(name, fieldName));
  const { errors } = useFormState({ control: formControl, name: fieldNames });
  const errorMessage = fieldNames
    .map((fieldName) => getErrorMessage(errors, fieldName))
    .find(Boolean);
  const describedBy = [
    description ? descriptionId : undefined,
    !hideError && errorMessage ? errorId : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <fieldset
      className={cn('space-y-2', className)}
      disabled={disabled}
      aria-describedby={describedBy || undefined}
      {...props}
    >
      <legend
        data-disabled={disabled}
        data-error={!!errorMessage}
        className="text-sm leading-none font-medium select-none data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-50 data-[error=true]:text-destructive"
      >
        {label}
      </legend>
      <div className="overflow-hidden rounded-lg border border-input bg-control [&>*+*]:border-t [&>*+*]:border-input">
        {children(formControl)}
      </div>
      {description && (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      )}
      {!hideError && errorMessage && (
        <p id={errorId} className="text-xs text-destructive">
          {errorMessage}
        </p>
      )}
    </fieldset>
  );
}

function AddressInputs<TFieldValues extends FieldValues>({
  control,
  name,
  disabled,
}: {
  control: Control<TFieldValues>;
  name?: FieldPath<TFieldValues>;
  disabled?: boolean;
}) {
  return (
    <>
      <AddressInputField
        control={control}
        name={getFieldName(name, 'address1')}
        label="Address line 1"
        placeholder="Address line 1"
        autoComplete="address-line1"
        disabled={disabled}
      />
      <AddressInputField
        control={control}
        name={getFieldName(name, 'address2')}
        label="Address line 2"
        placeholder="Address line 2"
        autoComplete="address-line2"
        disabled={disabled}
      />
      <div className="grid sm:grid-cols-2">
        <AddressInputField
          control={control}
          name={getFieldName(name, 'city')}
          label="City"
          placeholder="City"
          autoComplete="address-level2"
          disabled={disabled}
        />
        <AddressInputField
          control={control}
          name={getFieldName(name, 'state')}
          label="State"
          placeholder="State"
          autoComplete="address-level1"
          disabled={disabled}
          className="border-t border-input sm:border-t-0 sm:border-l"
        />
      </div>
      <div className="grid sm:grid-cols-2">
        <AddressInputField
          control={control}
          name={getFieldName(name, 'country')}
          label="Country"
          placeholder="Country"
          autoComplete="country-name"
          disabled={disabled}
        />
        <AddressInputField
          control={control}
          name={getFieldName(name, 'postcode')}
          label="Postcode"
          placeholder="Postcode"
          autoComplete="postal-code"
          disabled={disabled}
          className="border-t border-input sm:border-t-0 sm:border-l"
        />
      </div>
    </>
  );
}

function ContactAddressInputs<TFieldValues extends FieldValues>({
  control,
  name,
  disabled,
}: {
  control: Control<TFieldValues>;
  name?: FieldPath<TFieldValues>;
  disabled?: boolean;
}) {
  return (
    <>
      <AddressInputField
        control={control}
        name={getFieldName(name, 'companyName')}
        label="Company name"
        placeholder="Company name"
        autoComplete="organization"
        disabled={disabled}
      />
      <AddressInputField
        control={control}
        name={getFieldName(name, 'contactName')}
        label="Contact name"
        placeholder="Contact name"
        autoComplete="name"
        disabled={disabled}
      />
      <div className="grid sm:grid-cols-2">
        <AddressInputField
          control={control}
          name={getFieldName(name, 'contactNumber')}
          label="Phone"
          placeholder="Phone"
          autoComplete="tel"
          disabled={disabled}
        />
        <AddressInputField
          control={control}
          name={getFieldName(name, 'contactEmail')}
          label="Email"
          placeholder="Email"
          autoComplete="email"
          disabled={disabled}
          className="border-t border-input sm:border-t-0 sm:border-l"
        />
      </div>
      <AddressInputs control={control} name={name} disabled={disabled} />
    </>
  );
}

export function AddressFormField<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  label = 'Address',
  description,
  disabled,
  hideError,
  className,
  ...props
}: AddressFormFieldProps<TFieldValues>) {
  return (
    <AddressFormFieldFrame
      control={control}
      name={name}
      label={label}
      description={description}
      disabled={disabled}
      hideError={hideError}
      className={className}
      errorFields={addressFields}
      {...props}
    >
      {(formControl) => <AddressInputs control={formControl} name={name} disabled={disabled} />}
    </AddressFormFieldFrame>
  );
}

export function ContactAddressFormField<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  label = 'Address',
  description,
  disabled,
  hideError,
  className,
  ...props
}: AddressFormFieldProps<TFieldValues>) {
  return (
    <AddressFormFieldFrame
      control={control}
      name={name}
      label={label}
      description={description}
      disabled={disabled}
      hideError={hideError}
      className={className}
      errorFields={contactAddressFields}
      {...props}
    >
      {(formControl) => (
        <ContactAddressInputs control={formControl} name={name} disabled={disabled} />
      )}
    </AddressFormFieldFrame>
  );
}
